import { clsx } from "clsx";
import { HTMLAttributes, KeyboardEvent, ReactNode, useMemo } from "react";
import { Heading } from "../Typography/Heading";
import "./ArticleCard.css";

export interface ArticleCardProps
  extends Omit<HTMLAttributes<HTMLElement>, "title" | "onClick"> {
  /**
   * Title shown under the hero image.
   */
  title: ReactNode;
  /**
   * Author label displayed beneath the title.
   */
  author: ReactNode;
  /**
   * Source URL for the hero image.
   */
  imageSrc: string;
  /**
   * Accessible description for the hero image.
   */
  imageAlt?: string;
  /**
   * Background color for the media container.
   */
  backgroundColor?: string;
  /**
   * Click handler used for navigation.
   */
  onClick?: () => void;
  /**
   * Visual variant.
   * - `default`: centered hero illustration (knowledge-base articles).
   * - `cover`: split "deck cover" hero — a wordmark + display title on a light
   *   panel, illustration on a colored panel (Library portal guides / slide decks).
   * @default "default"
   */
  variant?: "default" | "cover";
  /**
   * Display title rendered inside the cover panel (cover variant only). Set in
   * the Society display font to mimic the deck's title slide. Falls back to
   * {@link title} when omitted.
   */
  coverTitle?: ReactNode;
  /**
   * Wordmark shown at the top-left of the cover panel (cover variant only).
   * @default "indigo"
   */
  wordmark?: ReactNode;
}

/**
 * ArticleCard component
 *
 * A compact summary card featuring a hero illustration, title, and author line.
 * Intended for knowledge-base or resource carousels.
 */
export const ArticleCard = ({
  title,
  author,
  imageSrc,
  imageAlt = "",
  backgroundColor = "var(--indigo-wash-sky)",
  onClick,
  className,
  tabIndex,
  role,
  variant = "default",
  coverTitle,
  wordmark = "indigo",
  ...rest
}: ArticleCardProps) => {
  const isCover = variant === "cover";
  const isInteractive = typeof onClick === "function";
  const resolvedTabIndex = useMemo(
    () => (tabIndex !== undefined ? tabIndex : isInteractive ? 0 : undefined),
    [isInteractive, tabIndex],
  );

  const mediaStyle = useMemo(() => ({ backgroundColor }), [backgroundColor]);
  const artStyle = useMemo(() => ({ backgroundColor }), [backgroundColor]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!isInteractive) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.();
    }
  };

  const handleClick = () => {
    onClick?.();
  };

  return (
    <article
      className={clsx(
        "indigo-article-card",
        isInteractive && "indigo-article-card--interactive",
        isCover && "indigo-article-card--cover",
        className,
      )}
      onClick={isInteractive ? handleClick : undefined}
      onKeyDown={handleKeyDown}
      role={isInteractive ? "button" : role}
      tabIndex={resolvedTabIndex}
      {...rest}
    >
      {isCover ? (
        <div className="indigo-article-card__cover">
          <div className="indigo-article-card__cover-text">
            <span className="indigo-article-card__cover-wordmark">
              {wordmark}
            </span>
            <Heading
              level={3}
              fontFamily="society"
              className="indigo-article-card__cover-title"
            >
              {coverTitle ?? title}
            </Heading>
          </div>
          <div
            className="indigo-article-card__cover-art"
            style={artStyle}
            aria-hidden={!imageAlt}
          >
            <img
              src={imageSrc}
              alt={imageAlt}
              loading="lazy"
              className="indigo-article-card__image"
            />
          </div>
        </div>
      ) : (
        <div
          className="indigo-article-card__media"
          style={mediaStyle}
          aria-hidden={!imageAlt}
        >
          <img
            src={imageSrc}
            alt={imageAlt}
            loading="lazy"
            className="indigo-article-card__image"
          />
        </div>
      )}
      <div className="indigo-article-card__content">
        <Heading level={2} className="indigo-article-card__title">
          {title}
        </Heading>
        <Heading level={4} className="indigo-article-card__author">
          {author}
        </Heading>
      </div>
    </article>
  );
};

ArticleCard.displayName = "ArticleCard";

export default ArticleCard;
